/**
 * Pure helpers for the footer build stamp — "which commit am I looking at?".
 *
 * Answers two questions at a glance: which deploy environment this is
 * (production / staging / dev) and exactly which build is live (version + short
 * commit SHA, linking to the commit on GitHub). Makes the staging→prod pipeline
 * legible: you can watch staging move to a new SHA while prod holds, and confirm
 * a deploy actually landed rather than guessing.
 *
 * The environment is derived from the host at runtime (not baked in) so the same
 * image serves prod and staging and self-labels correctly. Version + SHA are
 * baked at build time (see vite-env.d.ts / frontend/Dockerfile).
 *
 * Deterministic, no DOM, no clock — host is passed in.
 */

export type DeployEnv = "production" | "staging" | "dev";

/** GitHub repo the SHA links to. */
const REPO_URL = "https://github.com/frikyfriky11/pspulse";

/** Length of the abbreviated SHA shown in the UI (matches `git rev-parse --short`). */
const SHORT_SHA_LENGTH = 7;

export interface BuildInfo {
  /** Deploy environment, from the host. */
  env: DeployEnv;
  /** App version (e.g. "0.1.14") or null when not baked in (local dev). */
  version: string | null;
  /** Abbreviated commit SHA (7 chars) or null when not baked in. */
  shaShort: string | null;
  /** Link to the exact commit on GitHub, or null when no SHA is available. */
  commitUrl: string | null;
}

/**
 * Classifies the deploy environment from the host.
 *
 * - `pspulse-test.*` → staging (checked first: it also contains "pspulse")
 * - `pspulse.frikyfriky11.dev` → production
 * - anything else (localhost, IPs, previews) → dev
 */
export function deployEnv(host: string): DeployEnv {
  const h = host.toLowerCase();
  if (h.startsWith("pspulse-test.")) {
    return "staging";
  }
  if (h === "pspulse.frikyfriky11.dev") {
    return "production";
  }
  return "dev";
}

/**
 * Builds the {@link BuildInfo} shown in the footer from the runtime host and the
 * build-time env. A blank/whitespace version or SHA is treated as absent (CI
 * passes empty strings when a value is unavailable).
 */
export function buildInfo(
  host: string,
  version: string | undefined,
  sha: string | undefined,
): BuildInfo {
  const cleanVersion = version?.trim() ? version.trim() : null;
  const cleanSha = sha?.trim() ? sha.trim() : null;
  const shaShort = cleanSha ? cleanSha.slice(0, SHORT_SHA_LENGTH) : null;

  return {
    env: deployEnv(host),
    version: cleanVersion,
    shaShort,
    commitUrl: cleanSha ? `${REPO_URL}/commit/${cleanSha}` : null,
  };
}
